const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/u;

function escapeHtml(value) {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
}

function textValue(value) {
  return String(value ?? '').trim();
}

function translator(t) {
  return (key, fallback) => {
    if (typeof t !== 'function') return fallback;
    const value = t(key);
    return typeof value === 'string' && value.trim() && value !== key ? value : fallback;
  };
}

export function profileViewModel({ profile = {}, user = {}, role = '' } = {}) {
  const record = profile && typeof profile === 'object' && !Array.isArray(profile) ? profile : {};
  const account = user && typeof user === 'object' && !Array.isArray(user) ? user : {};
  return Object.freeze({
    identity: Object.freeze({
      loginEmail: textValue(account.email || record.account_login_email).toLowerCase(),
      role: textValue(role || record.role).toLowerCase(),
    }),
    contact: Object.freeze({
      display_name: textValue(record.display_name || account.displayName),
      phone: textValue(record.phone),
      contact_email: textValue(record.contact_email || record.real_email).toLowerCase(),
    }),
  });
}

export function buildContactUpdatePayload(formValues, { t } = {}) {
  const label = translator(t);
  const source = formValues && typeof formValues === 'object' && !Array.isArray(formValues)
    ? formValues : {};
  const displayName = textValue(source.display_name);
  if (!displayName) {
    return Object.freeze({ error: label('profile.displayNameRequired', 'Display name is required.') });
  }
  if (displayName.length > 200) {
    return Object.freeze({ error: label('profile.displayNameTooLong', 'Display name must be 200 characters or fewer.') });
  }
  const phone = textValue(source.phone);
  if (phone.length > 80) {
    return Object.freeze({ error: label('profile.phoneTooLong', 'Phone must be 80 characters or fewer.') });
  }
  const contactEmail = textValue(source.contact_email).toLowerCase();
  if (contactEmail && !EMAIL_PATTERN.test(contactEmail)) {
    return Object.freeze({ error: label('profile.contactEmailInvalid', 'Enter a valid contact email.') });
  }
  return Object.freeze({ display_name: displayName, phone, contact_email: contactEmail });
}

export function workStatusMarkup({ role = '', items = [], summary = [] } = {}, { t, locale = 'en' } = {}) {
  const label = translator(t);
  const entries = (Array.isArray(items) ? items : []).flatMap((value) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return [];
    return [{
      time: textValue(value.time) || '—',
      item: textValue(value.item) || '—',
      status: textValue(value.status) || '—',
    }];
  });
  const cards = (Array.isArray(summary) ? summary : []).flatMap((value) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return [];
    const cardLabel = textValue(value.label);
    if (!cardLabel) return [];
    const count = Number(value.value);
    return [{ label: cardLabel, value: Number.isFinite(count) ? count : 0 }];
  });
  const summaryStrip = cards.length
    ? `<div class="profile-page__cards" data-profile-work-summary>${cards.map((card) => (
      `<article class="profile-page__card"><strong>${card.value.toLocaleString(locale)}</strong><span>${escapeHtml(card.label)}</span></article>`
    )).join('')}</div>`
    : '';
  const body = entries.length
    ? entries.map((entry) => `<tr><td>${escapeHtml(entry.time)}</td><td>${escapeHtml(entry.item)}</td><td>${escapeHtml(entry.status)}</td></tr>`).join('')
    : `<tr><td colspan="3" class="profile-page__work-empty">${escapeHtml(label('profile.workStatusEmpty', 'No work records today.'))}</td></tr>`;
  return `<section class="profile-page__work-status" data-profile-work-status data-profile-work-role="${escapeHtml(textValue(role).toLowerCase())}" aria-labelledby="profileWorkStatusTitle">
    <header class="profile-page__section-heading"><h2 id="profileWorkStatusTitle">${escapeHtml(label('profile.workStatus', 'Work Status'))}</h2></header>
    ${summaryStrip}
    <div class="profile-page__work-table-wrap">
      <table class="profile-page__work-table">
        <thead><tr><th>${escapeHtml(label('profile.workTime', 'Time'))}</th><th>${escapeHtml(label('profile.workItem', 'Item'))}</th><th>${escapeHtml(label('profile.workState', 'Status'))}</th></tr></thead>
        <tbody>${body}</tbody>
      </table>
    </div>
  </section>`;
}

export function profilePageMarkup(viewModel, workStatusHtml = '', { t } = {}) {
  if (!viewModel || typeof viewModel !== 'object') {
    throw new TypeError('A profile view model is required.');
  }
  const label = translator(t);
  const identity = viewModel.identity || {};
  const contact = viewModel.contact || {};
  return `<section class="profile-page" data-profile-page>
    <header class="profile-page__heading"><h1>${escapeHtml(label('profile.title', 'My Profile'))}</h1></header>
    <div class="profile-page__layout">
      <section class="profile-page__identity" aria-labelledby="profileIdentityTitle">
        <header class="profile-page__section-heading"><h2 id="profileIdentityTitle">${escapeHtml(label('profile.account', 'Account'))}</h2></header>
        <dl class="profile-page__identity-list">
          <div><dt>${escapeHtml(label('profile.loginEmail', 'Login Email'))}</dt><dd>${escapeHtml(identity.loginEmail || '—')}</dd></div>
          <div><dt>${escapeHtml(label('profile.role', 'Role'))}</dt><dd>${escapeHtml(identity.role || '—')}</dd></div>
          <div><dt>${escapeHtml(label('profile.password', 'Password'))}</dt><dd class="profile-page__password-note">${escapeHtml(label('profile.passwordNote', '不可在此修改'))}</dd></div>
        </dl>
      </section>
      <section class="profile-page__contact" aria-labelledby="profileContactTitle">
        <header class="profile-page__section-heading"><h2 id="profileContactTitle">${escapeHtml(label('profile.contact', 'Contact'))}</h2></header>
        <form class="profile-page__contact-form" data-profile-contact-form novalidate>
          <label class="profile-page__field"><span>${escapeHtml(label('profile.displayName', 'Display Name'))}</span><input name="display_name" value="${escapeHtml(contact.display_name)}" maxlength="200" required></label>
          <label class="profile-page__field"><span>${escapeHtml(label('profile.phone', 'Phone'))}</span><input name="phone" value="${escapeHtml(contact.phone)}" maxlength="80" inputmode="tel"></label>
          <label class="profile-page__field"><span>${escapeHtml(label('profile.contactEmail', 'Contact Email'))}</span><input name="contact_email" value="${escapeHtml(contact.contact_email)}" maxlength="254" type="email"></label>
          <p class="profile-page__contact-error" data-profile-contact-error role="alert" aria-live="assertive"></p>
          <button type="submit" class="profile-page__contact-submit" data-profile-contact-submit>${escapeHtml(label('profile.save', 'Save Changes'))}</button>
        </form>
      </section>
    </div>
    ${workStatusHtml}
  </section>`;
}

export function mountProfilePage({ host, deps = {} } = {}) {
  const { loadProfile, saveContact, loadWorkStatus, t, locale = 'en' } = deps;
  if (!host || typeof loadProfile !== 'function' || typeof saveContact !== 'function'
    || typeof loadWorkStatus !== 'function') {
    return Object.freeze({ ready: Promise.resolve(), destroy() {} });
  }
  const label = translator(t);
  const controller = new AbortController();
  const { signal } = controller;
  let destroyed = false;

  const readContactValues = (form) => Object.fromEntries(
    ['display_name', 'phone', 'contact_email'].map((name) => [
      name,
      form.querySelector?.(`[name="${name}"]`)?.value ?? '',
    ]),
  );

  const wire = () => {
    const form = host.querySelector?.('[data-profile-contact-form]');
    if (!form) return;
    form.addEventListener('submit', async (event) => {
      event.preventDefault?.();
      const errorHost = host.querySelector?.('[data-profile-contact-error]');
      const submit = form.querySelector?.('[data-profile-contact-submit]');
      const payload = buildContactUpdatePayload(readContactValues(form), { t });
      if (payload.error) {
        if (errorHost) errorHost.textContent = payload.error;
        return;
      }
      if (errorHost) errorHost.textContent = '';
      if (submit) submit.disabled = true;
      try {
        await saveContact(payload);
        await render();
      } catch (error) {
        if (errorHost) errorHost.textContent = error?.message || label('profile.saveFailed', 'Contact update failed.');
        if (submit) submit.disabled = false;
      }
    }, { signal });
  };

  const render = async () => {
    const [loaded, workStatus] = await Promise.all([loadProfile(), loadWorkStatus()]);
    if (destroyed) return;
    const viewModel = profileViewModel(loaded && typeof loaded === 'object' ? loaded : {});
    const status = workStatus && typeof workStatus === 'object' ? workStatus : {};
    host.innerHTML = profilePageMarkup(viewModel, workStatusMarkup({
      role: viewModel.identity.role,
      items: status.items,
      summary: status.summary,
    }, { t, locale }), { t });
    wire();
  };

  const ready = render().catch((error) => {
    if (destroyed) return;
    host.innerHTML = `<p class="profile-page__load-error" role="alert">${escapeHtml(error?.message || label('profile.loadFailed', 'Your profile could not be loaded.'))}</p>`;
  });

  return Object.freeze({
    ready,
    destroy() {
      destroyed = true;
      controller.abort();
    },
  });
}
